import { useMemo, useState, useEffect } from "react";
import { useShallow } from "zustand/shallow";
import * as yup from "yup";
import dayjs from "dayjs";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { mdiCheck } from "@mdi/js";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import EventApi, { EventLocation, EventRow, Weather } from "api/event";
import AppBar from "components/AppBar";
import FAB from "components/FAB";
import AsyncAutocomplete from "components/AsyncAutocomplete";
import EmojiPicker, { DEFAULT_EMOJI, Emoji, EmojiSkin } from "components/EmojiPicker/EmojiPicker";
import LocationPickerDialog from "components/LocationPickerDialog";
import PageLayout from "components/PageLayout";
import useNotifier from "hooks/useNotifier";
import useBoundStore from "store";
import weatherAnimations from "assets/weather/weather-animations";
import styles from "./setEvent.module.scss";

const DATETIME_FORMAT = "DD/MM/YYYY HH:mm";
const validationSchema = yup.object().shape({
  eventIcon: yup.object().shape({
    aliases: yup.array().of(yup.string().required()),
    emoticons: yup.array().of(yup.string().required()),
    id: yup.string().required(),
    keywords: yup.array().of(yup.string().required()).required(),
    name: yup.string().required(),
    native: yup.string().required(),
    shortcodes: yup.string().required(),
    skin: yup.mixed<EmojiSkin>().oneOf([1, 2, 3, 4, 5, 6]),
    unified: yup.string().required(),
  }),
  location: yup
    .object()
    .shape({
      coordinates: yup.object().shape({
        lat: yup.number().required(),
        lng: yup.number().required(),
      }),
      name: yup.string().required(),
    })
    .nullable(),
  participantsLimit: yup.number().nullable(),
  getWeather: yup.boolean(),
  name: yup
    .string()
    .required("Event name is required")
    .min(2, "Name can't be shorter than 2 characters")
    .max(50, "Name can't be longer than 50 characters"),
  details: yup.string().max(2000),
  eventStartDate: yup
    .mixed<dayjs.Dayjs>()
    .required("Required")
    .test("eventStartDate", "'Event start date' must be at least 5 minutes from now", (value) =>
      value.isAfter(dayjs().add(5, "minutes"))
    ),
  eventEndDate: yup
    .mixed<dayjs.Dayjs>()
    .required("Required")
    .when("eventStartDate", ([eventStartDate], schema) =>
      eventStartDate
        ? schema.test(
            "eventEndDate",
            "'Event end date' must be later than 'Event start date'",
            (value) => value.isAfter(eventStartDate) || value.isSame(eventStartDate)
          )
        : schema
    ),
  voteBefore: yup
    .mixed<dayjs.Dayjs>()
    .required("Required")
    .when(["eventStartDate", "$datesChanged"], ([eventStartDate, datesChanged], schema) =>
      eventStartDate && datesChanged
        ? schema
            .test("voteBefore", "'Vote before' date can't be bigger than Event start date", (value) =>
              value.isBefore(eventStartDate)
            )
            .test(
              "voteBefore",
              "'Vote before' date must be at least 1 minute from now so user can have time to vote",
              (value) => value.isAfter(dayjs().add(1, "minutes"))
            )
        : schema
    ),
});

interface SetEventProps {
  onBack?: () => void;
  onSetEvent: () => void;
}

function SetEvent(props: SetEventProps) {
  const { onBack, onSetEvent } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [datesChanged, setDatesChanged] = useState(false);
  const selected = useBoundStore((state) => state.selected);
  const { events, groups } = useBoundStore(
    useShallow((state) => ({
      events: state.appData.events,
      groups: state.appData.groups,
    }))
  );
  const { showNotification } = useNotifier();
  const eventApi = useMemo(() => new EventApi(showNotification), [showNotification]);
  const event = events.find((e) => e.id === selected.eventId);
  const group = groups.find((g) => g.id === selected.groupId);

  const CloudsAnimation = weatherAnimations.Clouds;
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
    trigger,
    watch,
  } = useForm({
    context: { datesChanged },
    defaultValues: generateDefaultValues(),
    resolver: yupResolver(validationSchema),
  });
  const eventStartDate = watch("eventStartDate");
  const eventEndDate = watch("eventEndDate");
  const voteBefore = watch("voteBefore");
  const details = watch("details");
  const eventIcon = watch("eventIcon");
  const location = watch("location");

  useEffect(() => {
    setDatesChanged(
      !dayjs(eventStartDate).isSame(event?.start_date) ||
        !dayjs(eventEndDate).isSame(event?.end_date) ||
        !dayjs(voteBefore).isSame(event?.vote_before)
    );
  }, [eventEndDate, eventStartDate, voteBefore]);

  function generateDefaultValues() {
    const now = dayjs();

    return {
      details: event?.details ?? "",
      eventEndDate: event?.end_date ? dayjs(event?.end_date) : now.add(10, "minute"),
      eventIcon: event?.icon ?? DEFAULT_EMOJI,
      eventStartDate: event?.start_date ? dayjs(event?.start_date) : now.add(10, "minute"),
      location: event?.location ?? null,
      name: event?.name ?? "",
      participantsLimit: event?.participants_limit ?? 0,
      voteBefore: event?.vote_before ? dayjs(event?.vote_before) : now.add(5, "minute"),
      getWeather: Boolean(event?.weather),
    };
  }

  /**
   * Handle create/edit event action
   *
   * @param data
   */
  async function onSubmit(data: FormData) {
    setIsLoading(true);

    // Check if weather should be added
    let weather: Weather | null = (data.getWeather && event?.weather) || null;
    const shouldUpdateWeather =
      data.location &&
      data.getWeather &&
      (!event?.weather ||
        !dayjs(event.start_date).isSame(data.eventStartDate) ||
        !dayjs(event.end_date).isSame(data.eventEndDate));

    if (shouldUpdateWeather) {
      weather = {};
    }

    if (event) {
      // Update event
      const { error } = await eventApi.updateEvent(event.id, {
        details: data.details,
        end_date: data.eventEndDate.toDate().toUTCString(),
        icon: data.eventIcon,
        location: data.location,
        name: data.name,
        participants_limit: data.participantsLimit === 0 ? null : data.participantsLimit,
        start_date: data.eventStartDate.toDate().toUTCString(),
        status: "open",
        vote_before: data.voteBefore.toDate().toUTCString(),
        weather,
      });
      // Update weather
      if (shouldUpdateWeather) {
        await eventApi.updateWeather(event.id);
      }

      if (!error) {
        onSetEvent();
      } else {
        setIsLoading(false);
      }
    } else {
      // Create event
      const { event: createdEvent, error } = await eventApi.createEvent({
        details: data.details,
        end_date: data.eventEndDate.toDate().toUTCString(),
        group_id: selected.groupId!,
        icon: data.eventIcon,
        location: data.location,
        name: data.name,
        participants_limit: data.participantsLimit === 0 ? null : data.participantsLimit,
        start_date: data.eventStartDate.toDate().toUTCString(),
        vote_before: data.voteBefore.toDate().toUTCString(),
        weather,
      });
      // Update weather
      if (!error && createdEvent && shouldUpdateWeather) {
        await eventApi.updateWeather(createdEvent.id);
      }

      if (!error) {
        onSetEvent();
      } else {
        setIsLoading(false);
      }
    }
  }

  /**
   * Handle template change
   *
   * @param templateEvent
   */
  function handleTemplateChange(templateEvent: EventRow | null) {
    if (templateEvent && group) {
      setValue("name", templateEvent.name);
      setValue("details", templateEvent.details ?? "");
      if (templateEvent.participants_limit && templateEvent.participants_limit < group.users.length) {
        setValue("participantsLimit", templateEvent.participants_limit ?? 0);
      }
      setValue("location", templateEvent.location);
      setValue("getWeather", Boolean(templateEvent.weather));
      setValue("eventIcon", templateEvent.icon);

      clearErrors(["name", "details", "participantsLimit", "location", "getWeather", "eventIcon"]);
    }
  }

  async function fetchTemplates(name: string) {
    if (group?.id) {
      const eventTemplates = await eventApi.getEventTemplates(name, group.id);
      return eventTemplates;
    }

    return [];
  }

  return (
    <PageLayout
      appBar={<AppBar title={event ? "Save changes" : "Create event"} onBack={onBack} />}
      fab={
        <FAB
          aria-label={event ? "Save changes" : "Create event"}
          icon={mdiCheck}
          title='Check'
          loading={isLoading}
          onClick={handleSubmit(onSubmit)}
        />
      }
    >
      <Box p={2} pt={4}>
        <form autoComplete='off'>
          <Grid container spacing={4}>
            {event ? null : (
              <Grid item xs={12}>
                <AsyncAutocomplete
                  helperText='Automatically complete all inputs using an old event as a template'
                  label='Templates'
                  noOptionsText='Event not found.'
                  optionLabelKey='name'
                  fetchData={fetchTemplates}
                  onChange={handleTemplateChange}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Controller
                control={control}
                name='name'
                render={({ field }) => (
                  <TextField
                    {...field}
                    autoFocus={Boolean(event)}
                    error={Boolean(errors.name)}
                    fullWidth
                    helperText={errors.name?.message}
                    label='Event name'
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name='details'
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={Boolean(errors.details)}
                    fullWidth
                    helperText={`${details?.length}/2000`}
                    label='Event details'
                    maxRows={15}
                    minRows={4}
                    multiline
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Box mb={1}>
                <Typography color='text.secondary'>Event icon</Typography>
              </Box>

              <EmojiPicker value={eventIcon} onChange={(emoji) => setValue("eventIcon", emoji)} />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name='eventStartDate'
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    disablePast
                    format={DATETIME_FORMAT}
                    label='Event start date'
                    slotProps={{
                      textField: {
                        error: Boolean(errors.eventStartDate),
                        fullWidth: true,
                        helperText: errors.eventStartDate?.message,
                      },
                    }}
                    onChange={(value) => {
                      if (value) {
                        setValue("eventStartDate", value);
                        trigger("eventStartDate");
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name='eventEndDate'
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    disablePast
                    format={DATETIME_FORMAT}
                    label='Event end date'
                    slotProps={{
                      textField: {
                        error: Boolean(errors.eventEndDate),
                        fullWidth: true,
                        helperText: errors.eventEndDate?.message,
                      },
                    }}
                    onChange={(value) => {
                      if (value) {
                        setValue("eventEndDate", value);
                        trigger("eventEndDate");
                      }
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name='voteBefore'
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    disablePast
                    format={DATETIME_FORMAT}
                    label='Vote before'
                    slotProps={{
                      textField: {
                        error: Boolean(errors.voteBefore),
                        fullWidth: true,
                        helperText: errors.voteBefore?.message,
                      },
                    }}
                    onChange={(value) => {
                      if (value) {
                        setValue("voteBefore", value);
                        trigger("voteBefore");
                      }
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography color='text.secondary' mb={1}>
                Participants limit
              </Typography>
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name='participantsLimit'
                  render={({ field }) => (
                    <Select<number | null>
                      {...field}
                      displayEmpty
                      inputProps={{ "aria-label": "Participants limit" }}
                      onChange={(e) => {
                        const { value } = e.target;
                        if (typeof value === "number" || value === null) {
                          setValue("participantsLimit", value);
                        }
                      }}
                    >
                      <MenuItem value={0}>
                        <em>None</em>
                      </MenuItem>
                      {group && group.users.length > 1
                        ? Array(group.users.length - 1)
                            .fill(0)
                            .map((_, i) => (
                              <MenuItem key={i} value={i + 1}>
                                {i + 1}
                              </MenuItem>
                            ))
                        : null}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography color='text.secondary' mb={1}>
                Location
              </Typography>
              <LocationPickerDialog
                onSaveLocation={(newLocation: EventLocation | null) => setValue("location", newLocation)}
                location={location}
              />
            </Grid>

            {location ? (
              <Grid item xs={12}>
                <Typography color='text.secondary' mb={1}>
                  Weather
                </Typography>
                <FormGroup className={styles.weatherFormGroup}>
                  <FormControlLabel
                    control={
                      <Controller
                        control={control}
                        name='getWeather'
                        render={({ field }) => (
                          <Checkbox {...field} checked={field.value} inputProps={{ "aria-label": "controlled" }} />
                        )}
                      />
                    }
                    label={
                      <Box display='flex' alignItems='center'>
                        <CloudsAnimation />
                        <Typography color='text.secondary'>Show event weather</Typography>
                      </Box>
                    }
                    labelPlacement='start'
                  />
                </FormGroup>
              </Grid>
            ) : null}
          </Grid>
        </form>
      </Box>
    </PageLayout>
  );
}

export default SetEvent;

interface FormData {
  details?: string;
  eventEndDate: dayjs.Dayjs;
  eventIcon: Emoji;
  eventStartDate: dayjs.Dayjs;
  getWeather?: boolean;
  location: EventLocation | null;
  name: string;
  participantsLimit?: number | null;
  voteBefore: dayjs.Dayjs;
}
